"use client";

import { Button } from "@/components/shadcn/button";
import { Card, CardContent } from "@/components/shadcn/card";
import { Input } from "@/components/shadcn/input";
import { Label } from "@/components/shadcn/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/shadcn/select";
import { useSession } from "@/lib/auth/client";
import { FIDE_TITLES } from "@/lib/titles";
import { Camera } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

interface ApplicationInfo {
    Title: string;
    Status: string;
    ReviewNote: string | null;
    CreatedAt: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png"];

/**
 * Chess title verification application (#1058, design 2026-09-08): declared
 * name, FIDE ID, title and an ID+selfie photo, reviewed in the admin panel.
 * One pending application at a time.
 */
export default function TitleApplicationCard() {
    const t = useTranslations("account");
    const { data: session } = useSession();
    const [currentTitle, setCurrentTitle] = useState<string | null>(null);
    const [application, setApplication] = useState<ApplicationInfo | null>(
        null,
    );
    const [loaded, setLoaded] = useState(false);
    const [title, setTitle] = useState("");
    const [fideId, setFideId] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [file, setFile] = useState<globalThis.File | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const prefilled = useRef(false);

    useEffect(() => {
        let cancelled = false;
        fetch("/api/user/title-application")
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
                if (cancelled || !data) return;
                setCurrentTitle(data.title ?? null);
                setApplication(data.application ?? null);
            })
            .finally(() => {
                if (!cancelled) setLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Prefill the declared name from the profile once, still editable.
    useEffect(() => {
        if (prefilled.current || !session?.user) return;
        prefilled.current = true;
        setFirstName(session.user.name ?? "");
        setLastName(session.user.surname ?? "");
    }, [session]);

    const pending = application?.Status === "pending";
    const canSubmit =
        !!title && !!fideId.trim() && !!firstName.trim() && !!lastName.trim() &&
        !!file && !submitting;

    const acceptFile = (candidate: globalThis.File | undefined | null) => {
        if (!candidate) return;
        if (!ALLOWED_TYPES.includes(candidate.type)) {
            toast.error(t("identityFileType"));
            return;
        }
        if (candidate.size > MAX_FILE_SIZE) {
            toast.error(t("identityFileSize"));
            return;
        }
        setFile(candidate);
    };

    const submit = async () => {
        if (!canSubmit || !file) return;
        setSubmitting(true);
        try {
            const body = new FormData();
            body.append("title", title);
            body.append("fideId", fideId);
            body.append("firstName", firstName);
            body.append("lastName", lastName);
            body.append("identityFile", file);
            const res = await fetch("/api/user/title-application", {
                method: "POST",
                body,
            });
            if (res.status === 201) {
                const data = await res.json();
                setApplication({ ...data.application, ReviewNote: null });
                toast.success(t("titleApplicationSent"));
            } else if (res.status === 409) {
                const data = await res.json();
                toast.error(
                    data.error === "already_titled"
                        ? t("titleAlreadyHeld")
                        : t("titleApplicationPendingExists"),
                );
            } else {
                toast.error(t("titleApplicationFailed"));
            }
        } catch {
            toast.error(t("titleApplicationFailed"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card className="gap-0 overflow-hidden py-0">
            {/* Dark header, per the design */}
            <div className="flex items-center justify-between gap-4 bg-emerald-950 px-6 py-5 text-white">
                <div className="flex items-center gap-4">
                    <div className="flex size-11 items-center justify-center rounded-lg bg-white/10">
                        <span aria-hidden className="text-xl">
                            ♟
                        </span>
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold">
                            {t("titleApplicationTitle")}
                        </h2>
                        <p className="text-sm text-white/70">
                            {t("titleApplicationSubtitle")}
                        </p>
                    </div>
                </div>
                <span className="text-xs font-medium tracking-widest text-white/40">
                    {"SATRANÇ365" /* i18n-ignore: brand mark, per the design */}
                </span>
            </div>

            <CardContent className="flex flex-col gap-6 px-6 py-6">
                <div className="rounded-md border-l-4 border-emerald-600 bg-emerald-50 p-4 text-sm text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
                    {t("titleApplicationBanner")}
                </div>

                {currentTitle && (
                    <p className="text-sm">
                        {t("currentTitle")}{" "}
                        <span className="bg-primary/10 text-primary rounded px-1.5 py-0.5 font-semibold">
                            {currentTitle}
                        </span>
                    </p>
                )}

                {pending ? (
                    <p className="text-muted-foreground text-sm">
                        {t("titleApplicationPending", {
                            title: application!.Title,
                        })}
                    </p>
                ) : (
                    loaded && (
                        <>
                            {application?.Status === "rejected" && (
                                <p className="text-destructive text-sm">
                                    {t("titleApplicationRejected", {
                                        title: application.Title,
                                    })}
                                    {application.ReviewNote
                                        ? ` (${application.ReviewNote})`
                                        : ""}
                                </p>
                            )}

                            {/* Personal info */}
                            <div className="flex flex-col gap-4">
                                <h3 className="text-muted-foreground text-xs font-semibold tracking-widest uppercase">
                                    {t("personalInfoSection")}
                                </h3>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div className="flex flex-col gap-2">
                                        <Label htmlFor="title-first-name">
                                            {t("firstNameLabel")}{" "}
                                            <span className="text-destructive">
                                                *
                                            </span>
                                        </Label>
                                        <Input
                                            id="title-first-name"
                                            value={firstName}
                                            onChange={(e) =>
                                                setFirstName(e.target.value)
                                            }
                                            placeholder={t(
                                                "firstNamePlaceholder",
                                            )}
                                            maxLength={100}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <Label htmlFor="title-last-name">
                                            {t("lastNameLabel")}{" "}
                                            <span className="text-destructive">
                                                *
                                            </span>
                                        </Label>
                                        <Input
                                            id="title-last-name"
                                            value={lastName}
                                            onChange={(e) =>
                                                setLastName(e.target.value)
                                            }
                                            placeholder={t(
                                                "lastNamePlaceholder",
                                            )}
                                            maxLength={100}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <Label htmlFor="title-fide-id">
                                            FIDE ID{" "}
                                            <span className="text-destructive">
                                                *
                                            </span>
                                        </Label>
                                        <Input
                                            id="title-fide-id"
                                            value={fideId}
                                            onChange={(e) =>
                                                setFideId(e.target.value)
                                            }
                                            placeholder={t(
                                                "fideIdPlaceholder",
                                            )}
                                            maxLength={20}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <Label>
                                            {t("titleLabel")}{" "}
                                            <span className="text-destructive">
                                                *
                                            </span>
                                        </Label>
                                        <Select
                                            value={title}
                                            onValueChange={setTitle}
                                        >
                                            <SelectTrigger data-testid="title-select">
                                                <SelectValue
                                                    placeholder={t(
                                                        "titlePlaceholder",
                                                    )}
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {FIDE_TITLES.map((value) => (
                                                    <SelectItem
                                                        key={value}
                                                        value={value}
                                                    >
                                                        {value}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>

                            {/* Identity verification */}
                            <div className="flex flex-col gap-4">
                                <h3 className="text-muted-foreground text-xs font-semibold tracking-widest uppercase">
                                    {t("identitySection")}
                                </h3>
                                <div className="flex flex-col gap-2">
                                    <Label>
                                        {t("identityPhotoLabel")}{" "}
                                        <span className="text-destructive">
                                            *
                                        </span>
                                    </Label>
                                    <button
                                        type="button"
                                        data-testid="identity-dropzone"
                                        onClick={() =>
                                            fileInputRef.current?.click()
                                        }
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            setDragOver(true);
                                        }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            setDragOver(false);
                                            acceptFile(
                                                e.dataTransfer.files?.[0],
                                            );
                                        }}
                                        className={`flex flex-col items-center gap-1 rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                                            dragOver
                                                ? "border-primary bg-primary/5"
                                                : "border-border hover:border-muted-foreground/50"
                                        }`}
                                    >
                                        <Camera className="text-muted-foreground mb-1 size-7" />
                                        {file ? (
                                            <span className="font-medium">
                                                {file.name}
                                            </span>
                                        ) : (
                                            <>
                                                <span className="font-medium">
                                                    {t("identityUploadCta")}
                                                </span>
                                                <span className="text-muted-foreground text-sm">
                                                    {t("identityUploadDrag")}
                                                </span>
                                            </>
                                        )}
                                        <span className="text-muted-foreground text-xs">
                                            {t("identityUploadLimits")}
                                        </span>
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png"
                                        className="hidden"
                                        data-testid="identity-file-input"
                                        onChange={(e) =>
                                            acceptFile(e.target.files?.[0])
                                        }
                                    />
                                </div>
                                <div className="bg-muted/50 rounded-md border p-4 text-sm">
                                    <p className="mb-1 font-semibold">
                                        {t("identityHintTitle")}
                                    </p>
                                    <p className="text-muted-foreground">
                                        {t("identityHintBody")}
                                    </p>
                                </div>
                            </div>

                            <Button
                                onClick={submit}
                                disabled={!canSubmit}
                                variant="outline"
                                className="w-full"
                                data-testid="title-apply-button"
                            >
                                {t("titleApplyButton")}
                            </Button>
                            <p className="text-muted-foreground -mt-3 text-center text-xs">
                                {t("titlePrivacyNote")}
                            </p>
                        </>
                    )
                )}
            </CardContent>
        </Card>
    );
}
